import { getDatabase, type JnpDatabase } from './db';
import { classifyStorageError } from './errors';

/**
 * Small key/value store for device preferences (docs/08 “Four different state
 * domains”: preferences are neither project data nor session state). Values are plain
 * JSON; callers validate what they read, because stored data may come from an older
 * version of the app.
 */
export interface PreferencesRepository {
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
  /** Removes every key that starts with `prefix` (for example a purged project's guides). */
  removePrefix(prefix: string): Promise<void>;
}

export class DexiePreferencesRepository implements PreferencesRepository {
  private readonly dbProvider: () => JnpDatabase;

  constructor(dbProvider: () => JnpDatabase = getDatabase) {
    this.dbProvider = dbProvider;
  }

  async get(key: string): Promise<unknown> {
    try {
      return (await this.dbProvider().preferences.get(key))?.value;
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async set(key: string, value: unknown): Promise<void> {
    try {
      await this.dbProvider().preferences.put({ key, value });
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async remove(key: string): Promise<void> {
    try {
      await this.dbProvider().preferences.delete(key);
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async removePrefix(prefix: string): Promise<void> {
    try {
      await this.dbProvider().preferences.where('key').startsWith(prefix).delete();
    } catch (error) {
      throw classifyStorageError(error);
    }
  }
}

let repository: PreferencesRepository | null = null;

export function getPreferencesRepository(): PreferencesRepository {
  repository ??= new DexiePreferencesRepository();
  return repository;
}

export function setPreferencesRepositoryForTesting(repo: PreferencesRepository | null): void {
  repository = repo;
}
